import { useState, useEffect } from "react";
import { useMutation, useQuery, gql } from "@apollo/client";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiCamera,
  FiCreditCard,
  FiHeart,
  FiImage,
  FiMoreHorizontal,
  FiSearch,
  FiShoppingCart,
  FiStar,
  FiPackage,
  FiCoffee,
} from "react-icons/fi";

const GET_CAMPAIGN = gql`
  query MissionAdminCampaignForEdit($campaignId: Int!) {
    missionAdminCampaign(campaignId: $campaignId) {
      id
      brand {
        name
      }
      channel
      category
      productName
      productImageUrl
      productUrl
      originalPrice
      discountPrice
      photoReviewRequired
      eatingMethods
      steps {
        id
        stepOrder
        type
        title
        guide
        requirements
        notices
        proofType
        isRequired
        meta
        windowStartAt
        windowEndAt
        imageUrl
      }
      runs {
        id
        startAt
        endAt
        durationMinutes
        depositAmount
        prizeAmount
        note
        isActive
        maxParticipants
        currentParticipants
        isFull
        remainingSlots
        participationCount
      }
    }
  }
`;

const UPDATE_CAMPAIGN_BASIC = gql`
  mutation MissionAdminUpdateCampaignBasic(
    $campaignId: Int!
    $input: MissionAdminUpdateCampaignBasicInput!
  ) {
    missionAdminUpdateCampaignBasic(campaignId: $campaignId, input: $input) {
      id
      brand {
        name
      }
      productName
      channel
    }
  }
`;

const SET_MISSION_STEPS = gql`
  mutation MissionAdminSetMissionSteps(
    $campaignId: Int!
    $steps: [MissionAdminMissionStepInput!]!
  ) {
    missionAdminSetMissionSteps(campaignId: $campaignId, steps: $steps) {
      id
      stepOrder
      type
      title
    }
  }
`;

const GET_TEMPLATES = gql`
  query MissionAdminMissionStepTemplatesForEdit(
    $channel: Channel
    $missionType: MissionType
  ) {
    missionAdminMissionStepTemplates(
      channel: $channel
      missionType: $missionType
    ) {
      id
      missionType
      channel
      title
      guide
      requirements
      notices
      proofType
    }
  }
`;

const GET_BRANDS = gql`
  query MissionAdminBrandsForEdit {
    missionAdminBrands {
      id
      name
    }
  }
`;

const CREATE_RUN = gql`
  mutation MissionAdminCreateRunForEdit(
    $campaignId: Int!
    $input: MissionAdminCreateRunInput!
  ) {
    missionAdminCreateRun(campaignId: $campaignId, input: $input) {
      id
      startAt
      endAt
      depositAmount
      prizeAmount
      maxParticipants
      currentParticipants
      isFull
      remainingSlots
    }
  }
`;

const UPDATE_RUN = gql`
  mutation MissionAdminUpdateRun(
    $runId: Int!
    $input: MissionAdminUpdateRunInput!
  ) {
    missionAdminUpdateRun(runId: $runId, input: $input) {
      id
      startAt
      endAt
      depositAmount
      prizeAmount
      isActive
      maxParticipants
      currentParticipants
      isFull
      remainingSlots
    }
  }
`;

const DELETE_RUN = gql`
  mutation MissionAdminDeleteRun($runId: Int!) {
    missionAdminDeleteRun(runId: $runId) {
      ok
      error
    }
  }
`;

const MissionAdminCampaignsEdit = () => {
  const { campaignId: campaignIdParam } = useParams<{ campaignId: string }>();
  const campaignId = campaignIdParam ? parseInt(campaignIdParam) : 0;
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [editingRunId, setEditingRunId] = useState<number | null>(null);
  const [isCreatingRun, setIsCreatingRun] = useState(false);
  const [runInfo, setRunInfo] = useState({
    runType: "separate" as "separate" | "continuous" | "timeSelect",
    selectedDates: [] as string[],
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    depositAmount: "",
    note: "",
    maxParticipantsType: "unlimited" as "unlimited" | "limited",
    maxParticipantsValue: "",
  });

  const {
    data: campaignData,
    loading: loadingCampaign,
    refetch: refetchCampaign,
  } = useQuery(GET_CAMPAIGN, {
    variables: { campaignId },
    skip: !campaignId,
  });

  // Step 1: 기본 정보
  const [basicInfo, setBasicInfo] = useState<{
    brandName: string;
    channel: string;
    productName: string;
    productImageUrl: File | string | null;
    productUrl: string;
    originalPrice: string;
    discountPrice: string;
    photoReviewRequired: boolean;
    eatingMethods: string[];
  }>({
    brandName: "",
    channel: "",
    productName: "",
    productImageUrl: null,
    productUrl: "",
    originalPrice: "",
    discountPrice: "",
    photoReviewRequired: false,
    eatingMethods: [],
  });
  const productUrlHref = (() => {
    const url = basicInfo.productUrl.trim();
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
  })();

  // Step 3: 미션 단계
  const [missionSteps, setMissionSteps] = useState<any[]>([]);
  const [selectedMissionTypes, setSelectedMissionTypes] = useState<string[]>(
    []
  );

  // 미션 타입 목록
  const missionTypeOptions = [
    { value: "SEARCH", label: "검색", Icon: FiSearch },
    { value: "LIKE", label: "찜", Icon: FiHeart },
    { value: "ADD_TO_CART", label: "장바구니", Icon: FiShoppingCart },
    { value: "PURCHASE", label: "구매", Icon: FiCreditCard },
    { value: "REVIEW", label: "리뷰", Icon: FiStar },
    { value: "PHOTO_REVIEW", label: "포토리뷰", Icon: FiImage },
    { value: "USAGE_SHOT", label: "사용샷 인증", Icon: FiCamera },
    { value: "ETC", label: "기타", Icon: FiMoreHorizontal },
  ];

  // 미션 타입 카드 선택/해제
  const handleMissionTypeToggle = (missionType: string) => {
    const isSelected = selectedMissionTypes.includes(missionType);

    if (isSelected) {
      setSelectedMissionTypes((prev) => prev.filter((t) => t !== missionType));
      setMissionSteps((prev) =>
        prev.filter((step) => step.type !== missionType)
      );
      return;
    }

    // 선택: 새로운 미션 스텝 추가
    const template = templatesData?.missionAdminMissionStepTemplates?.find(
      (t: any) =>
        t.missionType === missionType &&
        (t.channel === basicInfo.channel || !t.channel)
    );

    // 특정 미션 타입에 따라 proofType 자동 설정
    let proofType = template?.proofType || "NONE";
    if (missionType === "USAGE_SHOT") {
      proofType = "PHOTO";
    } else if (
      missionType === "ADD_TO_CART" ||
      missionType === "REVIEW" ||
      missionType === "PHOTO_REVIEW"
    ) {
      proofType = "SCREENSHOT";
    }

    const newStep: any = {
      type: missionType,
      title: template?.title || "",
      guide: template?.guide || [],
      requirements: template?.requirements || [],
      notices: template?.notices || [],
      proofType: proofType,
      isRequired: true,
      meta: null,
      imageUrl: null,
    };

    setSelectedMissionTypes((prev) => [...prev, missionType]);
    setMissionSteps((prev) => [...prev, newStep]);
  };

  const [updateBasic, { loading: updatingBasic }] = useMutation(
    UPDATE_CAMPAIGN_BASIC,
    {
      refetchQueries: [{ query: GET_CAMPAIGN, variables: { campaignId } }],
    }
  );
  const [setSteps, { loading: settingSteps }] = useMutation(SET_MISSION_STEPS);
  const [createRun, { loading: creatingRun }] = useMutation(CREATE_RUN, {
    onCompleted: () => {
      refetchCampaign();
    },
  });
  const [updateRun, { loading: updatingRun }] = useMutation(UPDATE_RUN, {
    onCompleted: () => {
      refetchCampaign();
    },
  });
  const [deleteRun, { loading: deletingRun }] = useMutation(DELETE_RUN, {
    onCompleted: () => {
      refetchCampaign();
    },
  });

  const { data: templatesData } = useQuery(GET_TEMPLATES, {
    variables: {
      channel: basicInfo.channel || undefined,
    },
    skip: !basicInfo.channel,
  });

  const { data: brandsData } = useQuery(GET_BRANDS);

  // 캠페인 데이터 로드 시 폼 초기화
  useEffect(() => {
    if (campaignData?.missionAdminCampaign) {
      const campaign = campaignData.missionAdminCampaign;
      setBasicInfo({
        brandName: campaign.brand.name,
        channel: campaign.channel,
        productName: campaign.productName,
        productImageUrl: campaign.productImageUrl || null, // 기존 URL은 string으로 유지
        productUrl: campaign.productUrl || "",
        originalPrice: campaign.originalPrice.toString(),
        discountPrice: campaign.discountPrice.toString(),
        photoReviewRequired: campaign.photoReviewRequired,
        eatingMethods: campaign.eatingMethods || [],
      });

      // 미션 스텝 정렬하여 설정
      const sortedSteps = [...campaign.steps].sort(
        (a: any, b: any) => a.stepOrder - b.stepOrder
      );
      // 선택된 미션 타입 동기화 (stepOrder 순서 기준)
      const initialTypes: string[] = [];
      sortedSteps.forEach((s: any) => {
        if (!initialTypes.includes(s.type)) initialTypes.push(s.type);
      });
      setSelectedMissionTypes(initialTypes);
      setMissionSteps(
        sortedSteps.map((step: any, idx: number) => ({
          type: step.type,
          title: step.title || "",
          guide: step.guide || [],
          requirements: step.requirements || [],
          notices: step.notices || [],
          proofType: step.proofType,
          isRequired: step.isRequired ?? true,
          meta: step.meta || null,
          imageUrl: step.imageUrl || null,
          // windowStartAt과 windowEndAt은 서버에서 run의 startAt/endAt을 사용하므로 로컬 상태에서 제거
        }))
      );
    }
  }, [campaignData]);

  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (basicInfo.eatingMethods.length === 0) {
      alert("먹는 방식을 최소 하나 이상 선택해주세요.");
      return;
    }

    const paybackPrice =
      parseInt(basicInfo.originalPrice) - parseInt(basicInfo.discountPrice);

    if (paybackPrice <= 0) {
      alert("정가가 할인가보다 커야 합니다.");
      return;
    }

    try {
      await updateBasic({
        variables: {
          campaignId,
          input: {
            brandName: basicInfo.brandName,
            channel: basicInfo.channel,
            category: null,
            productName: basicInfo.productName,
            productImageUrl:
              basicInfo.productImageUrl instanceof File
                ? basicInfo.productImageUrl
                : basicInfo.productImageUrl === null
                ? null
                : undefined, // string인 경우(기존 URL) undefined로 전달하여 변경하지 않음
            productUrl: basicInfo.productUrl || null,
            originalPrice: parseInt(basicInfo.originalPrice),
            discountPrice: parseInt(basicInfo.discountPrice),
            photoReviewRequired: basicInfo.photoReviewRequired,
            eatingMethods:
              basicInfo.eatingMethods.length > 0
                ? basicInfo.eatingMethods
                : null,
          },
        },
      });

      alert("기본 정보가 수정되었습니다.");
      setStep(2); // 회차 관리로 이동
    } catch (error: any) {
      alert(`에러: ${error.message}`);
    }
  };

  const handleStep3Submit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (missionSteps.length === 0) {
      alert("최소 1개 이상의 미션 단계가 필요합니다.");
      return;
    }

    // 참여자가 있는 회차가 있으면 저장 불가
    const canEditGuide =
      !campaignData?.missionAdminCampaign?.runs ||
      campaignData.missionAdminCampaign.runs.length === 0 ||
      campaignData.missionAdminCampaign.runs.every(
        (run: any) => run.participationCount === 0
      );

    if (!canEditGuide) {
      alert(
        "참여자가 있는 회차가 있어 미션 단계를 수정할 수 없습니다. 모든 회차의 참여자가 없을 때만 수정 가능합니다."
      );
      return;
    }

    try {
      await setSteps({
        variables: {
          campaignId,
          steps: missionSteps.map((step, idx) => ({
            stepOrder: idx + 1,
            type: step.type,
            title: step.title || null,
            guide: step.guide || [],
            requirements: step.requirements || [],
            notices: step.notices || [],
            proofType: step.proofType,
            isRequired: step.isRequired ?? true,
            meta: step.meta || null,
            imageUrl: step.imageUrl || null,
            // windowStartAt과 windowEndAt은 서버에서 run의 startAt/endAt을 사용하므로 전달하지 않음
          })),
        },
      });

      alert("미션 단계가 수정되었습니다.");
      // 편집 완료 후 상세 페이지로 이동
      navigate(`/ops/mission/campaigns/${campaignId}`);
    } catch (error: any) {
      alert(`에러: ${error.message}`);
    }
  };

  const handleRunCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!campaignId || campaignId === 0) {
      alert("캠페인 ID가 유효하지 않습니다.");
      return;
    }

    try {
      // 캠페인의 페이백 금액 사용
      const paybackPrice =
        parseInt(basicInfo.originalPrice) - parseInt(basicInfo.discountPrice);

      // maxParticipants 처리
      const maxParticipants =
        runInfo.maxParticipantsType === "limited" &&
        runInfo.maxParticipantsValue
          ? parseInt(runInfo.maxParticipantsValue)
          : null;

      if (runInfo.runType === "separate") {
        // 여러날 따로 생성: 선택한 날짜들 각각에 대해 회차 생성
        const validDates = runInfo.selectedDates.filter((date) => date !== "");
        if (validDates.length === 0) {
          alert("최소 하나 이상의 날짜를 선택해주세요.");
          return;
        }

        // 각 날짜에 대해 회차 생성
        for (const dateStr of validDates) {
          const selectedDate = new Date(dateStr);
          selectedDate.setHours(0, 0, 0, 0);
          const startAtISO = selectedDate.toISOString();

          const endDate = new Date(dateStr);
          endDate.setDate(endDate.getDate() + 1); // 다음날
          endDate.setHours(0, 0, 0, 0);
          const endAtISO = endDate.toISOString();

          await createRun({
            variables: {
              campaignId,
              input: {
                startAt: startAtISO,
                endAt: endAtISO,
                durationMinutes: null,
                depositAmount: parseInt(runInfo.depositAmount),
                prizeAmount: paybackPrice,
                note: runInfo.note || null,
                maxParticipants,
              },
            },
          });
        }
      } else if (runInfo.runType === "continuous") {
        // 여러날 이어서 생성: 하나의 회차 생성
        if (!runInfo.startDate || !runInfo.endDate) {
          alert("시작일과 종료일을 모두 입력해주세요.");
          return;
        }
        const startDate = new Date(runInfo.startDate);
        startDate.setHours(0, 0, 0, 0);
        const startAtISO = startDate.toISOString();

        const endDate = new Date(runInfo.endDate);
        endDate.setDate(endDate.getDate() + 1); // 다음날
        endDate.setHours(0, 0, 0, 0);
        const endAtISO = endDate.toISOString();

        await createRun({
          variables: {
            campaignId,
            input: {
              startAt: startAtISO,
              endAt: endAtISO,
              durationMinutes: null,
              depositAmount: parseInt(runInfo.depositAmount),
              prizeAmount: paybackPrice,
              note: runInfo.note || null,
              maxParticipants,
            },
          },
        });
      } else {
        // 커스터마이징: 날짜 + 시간 지정
        if (
          !runInfo.startDate ||
          !runInfo.endDate ||
          !runInfo.startTime ||
          !runInfo.endTime
        ) {
          alert("시작일, 종료일, 시작 시간, 종료 시간을 모두 입력해주세요.");
          return;
        }
        const startDate = new Date(
          `${runInfo.startDate}T${runInfo.startTime}:00`
        );
        const startAtISO = startDate.toISOString();

        const endDate = new Date(`${runInfo.endDate}T${runInfo.endTime}:00`);
        const endAtISO = endDate.toISOString();

        await createRun({
          variables: {
            campaignId,
            input: {
              startAt: startAtISO,
              endAt: endAtISO,
              durationMinutes: null,
              depositAmount: parseInt(runInfo.depositAmount),
              prizeAmount: paybackPrice,
              note: runInfo.note || null,
              maxParticipants,
            },
          },
        });
      }

      alert("회차가 생성되었습니다.");
      // 폼 초기화
      setRunInfo({
        runType: "separate",
        selectedDates: [],
        startDate: "",
        endDate: "",
        startTime: "",
        endTime: "",
        depositAmount: "",
        note: "",
        maxParticipantsType: "unlimited",
        maxParticipantsValue: "",
      });
      setIsCreatingRun(false);
    } catch (error: any) {
      console.error("Run 생성 에러:", error);
      alert(`에러: ${error.message || JSON.stringify(error)}`);
    }
  };

  const handleRunUpdate = async (e: React.FormEvent, runId: number) => {
    e.preventDefault();

    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const isFullDay = formData.get("isFullDay") === "on";
    const startDate = formData.get("startDate") as string;
    const endDate = formData.get("endDate") as string;
    const startTime = formData.get("startTime") as string;
    const endTime = formData.get("endTime") as string;
    const depositAmount = formData.get("depositAmount") as string;
    const prizeAmount = formData.get("prizeAmount") as string;
    const note = formData.get("note") as string;

    // maxParticipants 처리
    const maxParticipantsRadio = form.querySelector(
      `input[name="maxParticipants-${runId}"]:checked`
    ) as HTMLInputElement;
    const maxParticipantsValue = formData.get("maxParticipantsValue") as string;
    const maxParticipants =
      maxParticipantsRadio?.value === "limited" && maxParticipantsValue
        ? parseInt(maxParticipantsValue)
        : null;

    if (!startDate || !endDate || !depositAmount || !prizeAmount) {
      alert("필수 필드를 모두 입력해주세요.");
      return;
    }

    if (!isFullDay && (!startTime || !endTime)) {
      alert("시작 시간과 종료 시간을 모두 입력해주세요.");
      return;
    }

    try {
      let startAtISO: string;
      let endAtISO: string;

      if (isFullDay) {
        // 하루 통일인 경우: 시작일 00:00 ~ 종료일 00:00
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        startAtISO = start.toISOString();

        const end = new Date(endDate);
        end.setDate(end.getDate() + 1); // 다음날
        end.setHours(0, 0, 0, 0);
        endAtISO = end.toISOString();
      } else {
        // 일반 챌린지인 경우: 날짜 + 시간 (분은 00으로 고정)
        const start = new Date(`${startDate}T${startTime}:00`);
        startAtISO = start.toISOString();

        const end = new Date(`${endDate}T${endTime}:00`);
        endAtISO = end.toISOString();
      }

      await updateRun({
        variables: {
          runId,
          input: {
            startAt: startAtISO,
            endAt: endAtISO,
            durationMinutes: null,
            depositAmount: parseInt(depositAmount),
            prizeAmount: parseInt(prizeAmount),
            note: note || null,
            maxParticipants,
          },
        },
      });
      alert("회차가 수정되었습니다.");
      setEditingRunId(null);
    } catch (error: any) {
      console.error("Run 수정 에러:", error);
      alert(`에러: ${error.message || JSON.stringify(error)}`);
    }
  };

  const handleRunDelete = async (runId: number) => {
    if (
      !window.confirm(
        "정말 이 회차를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다."
      )
    ) {
      return;
    }

    try {
      const { data } = await deleteRun({
        variables: { runId },
      });

      if (data.missionAdminDeleteRun.ok) {
        alert("회차가 삭제되었습니다.");
      } else {
        alert(`삭제 실패: ${data.missionAdminDeleteRun.error}`);
      }
    } catch (error: any) {
      alert(`에러: ${error.message}`);
    }
  };

  if (loadingCampaign) return <div>로딩 중...</div>;
  if (!campaignData?.missionAdminCampaign) {
    return <div>캠페인을 찾을 수 없습니다.</div>;
  }

  const campaign = campaignData.missionAdminCampaign;

  if (campaign.isPublished) {
    return (
      <div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-yellow-800">
            공개된 캠페인은 편집할 수 없습니다. 비공개로 변경한 후 편집해주세요.
          </p>
        </div>
        <button
          onClick={() => navigate(`/ops/mission/campaigns/${campaignId}`)}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
        >
          돌아가기
        </button>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">캠페인 편집</h1>

      {/* 진행 단계 표시 */}
      <div className="mb-8">
        <div className="flex items-center">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step >= s
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {s}
                </div>
                <span className="mt-2 text-xs text-gray-600 text-center">
                  {s === 1 && "기본 정보"}
                  {s === 2 && "회차 관리"}
                  {s === 3 && "미션 단계"}
                </span>
              </div>
              {s < 3 && (
                <div
                  className={`flex-1 h-1 mx-2 ${
                    step > s ? "bg-blue-600" : "bg-gray-200"
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: 기본 정보 */}
      {step === 1 && (
        <form
          onSubmit={handleStep1Submit}
          className="bg-white p-6 rounded-lg shadow max-w-2xl mx-auto"
        >
          <h2 className="text-xl font-semibold mb-4">기본 정보 수정</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                브랜드 *
              </label>
              <select
                required
                value={basicInfo.brandName}
                onChange={(e) =>
                  setBasicInfo({ ...basicInfo, brandName: e.target.value })
                }
                className="w-full px-3 py-3 border border-gray-300 rounded-md"
              >
                <option value="">선택하세요</option>
                {brandsData?.missionAdminBrands?.map((brand: any) => (
                  <option key={brand.id} value={brand.name}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                채널 *
              </label>
              <select
                required
                value={basicInfo.channel}
                onChange={(e) =>
                  setBasicInfo({ ...basicInfo, channel: e.target.value })
                }
                className="w-full px-3 py-3 border border-gray-300 rounded-md"
              >
                <option value="">선택하세요</option>
                <option value="NAVER">네이버</option>
                <option value="COUPANG">쿠팡</option>
                <option value="CARROT">당근</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                제품명 *
              </label>
              <input
                type="text"
                required
                value={basicInfo.productName}
                onChange={(e) =>
                  setBasicInfo({ ...basicInfo, productName: e.target.value })
                }
                className="w-full px-3 py-3 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                제품 이미지
              </label>
              <div className="space-y-3">
                {/* 기존 이미지 또는 선택한 파일 미리보기 표시 */}
                {basicInfo.productImageUrl && (
                  <div className="relative">
                    <img
                      src={
                        typeof basicInfo.productImageUrl === "string"
                          ? basicInfo.productImageUrl
                          : URL.createObjectURL(basicInfo.productImageUrl)
                      }
                      alt="제품 이미지"
                      className="w-full max-w-md rounded-lg object-cover border border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        // File 객체인 경우 URL 해제
                        if (basicInfo.productImageUrl instanceof File) {
                          URL.revokeObjectURL(
                            URL.createObjectURL(basicInfo.productImageUrl)
                          );
                        }
                        setBasicInfo({
                          ...basicInfo,
                          productImageUrl: null,
                        });
                      }}
                      className="mt-2 px-3 py-1 text-sm text-red-600 hover:text-red-800 border border-red-300 rounded hover:bg-red-50"
                    >
                      이미지 삭제
                    </button>
                  </div>
                )}
                {/* 파일 업로드 */}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // 기존 File 객체가 있으면 URL 해제
                        if (basicInfo.productImageUrl instanceof File) {
                          URL.revokeObjectURL(
                            URL.createObjectURL(basicInfo.productImageUrl)
                          );
                        }
                        setBasicInfo({
                          ...basicInfo,
                          productImageUrl: file,
                        });
                      }
                    }}
                    className="block w-full text-sm text-gray-700 border border-gray-300 rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    이미지 파일을 선택하세요 (JPG, PNG 등)
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                제품 바로가기 URL
              </label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={basicInfo.productUrl}
                  onChange={(e) =>
                    setBasicInfo({ ...basicInfo, productUrl: e.target.value })
                  }
                  className="flex-1 px-3 py-3 border border-gray-300 rounded-md"
                />
                <button
                  type="button"
                  disabled={!productUrlHref}
                  onClick={() => {
                    if (!productUrlHref) return;
                    window.open(productUrlHref, "_blank", "noreferrer");
                  }}
                  className={`px-4 py-3 rounded-md text-sm whitespace-nowrap ${
                    productUrlHref
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  미리보기
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  정가 (원) *
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  value={basicInfo.originalPrice}
                  onChange={(e) =>
                    setBasicInfo({
                      ...basicInfo,
                      originalPrice: e.target.value,
                    })
                  }
                  className="w-full px-3 py-3 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  할인가 (원) *
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  value={basicInfo.discountPrice}
                  onChange={(e) =>
                    setBasicInfo({
                      ...basicInfo,
                      discountPrice: e.target.value,
                    })
                  }
                  className="w-full px-3 py-3 border border-gray-300 rounded-md"
                />
              </div>
            </div>

            {basicInfo.originalPrice && basicInfo.discountPrice && (
              <div className="p-4 bg-blue-50 rounded-md">
                <div className="text-sm text-gray-700">
                  페이백 금액:{" "}
                  <span className="font-bold text-blue-600">
                    {(
                      parseInt(basicInfo.originalPrice) -
                      parseInt(basicInfo.discountPrice)
                    ).toLocaleString()}
                    원
                  </span>
                </div>
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="photoReviewRequired"
                checked={basicInfo.photoReviewRequired}
                onChange={(e) =>
                  setBasicInfo({
                    ...basicInfo,
                    photoReviewRequired: e.target.checked,
                  })
                }
                className="mr-2"
              />
              <label
                htmlFor="photoReviewRequired"
                className="text-sm font-medium text-gray-700"
              >
                포토리뷰 필수
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                먹는 방식 *{" "}
                <span className="text-gray-500 font-normal">
                  (복수 선택가능)
                </span>
              </label>
              <p className="text-xs text-gray-500 mb-2">*유저에게 노출됩니다</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    const isSelected = basicInfo.eatingMethods.includes("포장");
                    if (isSelected) {
                      setBasicInfo({
                        ...basicInfo,
                        eatingMethods: basicInfo.eatingMethods.filter(
                          (m) => m !== "포장"
                        ),
                      });
                    } else {
                      setBasicInfo({
                        ...basicInfo,
                        eatingMethods: [...basicInfo.eatingMethods, "포장"],
                      });
                    }
                  }}
                  className={`px-4 py-3 border-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    basicInfo.eatingMethods.includes("포장")
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                  }`}
                >
                  <FiPackage className="text-base" />
                  <span>포장</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const isSelected =
                      basicInfo.eatingMethods.includes("매장식사");
                    if (isSelected) {
                      setBasicInfo({
                        ...basicInfo,
                        eatingMethods: basicInfo.eatingMethods.filter(
                          (m) => m !== "매장식사"
                        ),
                      });
                    } else {
                      setBasicInfo({
                        ...basicInfo,
                        eatingMethods: [...basicInfo.eatingMethods, "매장식사"],
                      });
                    }
                  }}
                  className={`px-4 py-3 border-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    basicInfo.eatingMethods.includes("매장식사")
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                  }`}
                >
                  <FiCoffee className="text-base" />
                  <span>매장식사</span>
                </button>
              </div>
              {basicInfo.eatingMethods.length === 0 && (
                <p className="mt-1 text-xs text-red-500">
                  최소 하나 이상 선택해주세요.
                </p>
              )}
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={updatingBasic}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {updatingBasic ? "저장 중..." : "다음 단계"}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Step 2: 회차 관리 */}
      {step === 2 && campaignData?.missionAdminCampaign && (
        <div className="bg-white p-6 rounded-lg shadow max-w-2xl mx-auto">
          <h2 className="text-xl font-semibold mb-4">회차 관리</h2>

          {/* 기존 회차 목록 (회차 추가 폼이 열렸을 때만 표시) */}
          {isCreatingRun &&
            campaignData.missionAdminCampaign.runs &&
            campaignData.missionAdminCampaign.runs.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-3 text-gray-700">
                  기존 회차 ({campaignData.missionAdminCampaign.runs.length}개)
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                  {[...campaignData.missionAdminCampaign.runs]
                    .sort(
                      (a: any, b: any) =>
                        new Date(a.startAt).getTime() -
                        new Date(b.startAt).getTime()
                    )
                    .map((run: any) => {
                      const now = new Date();
                      const startAt = new Date(run.startAt);
                      const endAt = new Date(run.endAt);
                      let status = "예정";
                      if (now >= startAt && now <= endAt) status = "진행 중";
                      else if (now > endAt) status = "종료";
                      else if (
                        startAt.getTime() - now.getTime() <=
                        60 * 60 * 1000
                      )
                        status = "모집 마감";

                      return (
                        <div
                          key={run.id}
                          className="border border-gray-300 rounded-lg p-3 bg-white"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-medium text-sm">
                                {new Date(run.startAt).toLocaleString("ko-KR")}{" "}
                                ~ {new Date(run.endAt).toLocaleString("ko-KR")}
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                예치금: {run.depositAmount.toLocaleString()}원 |
                                상금: {run.prizeAmount.toLocaleString()}원
                              </div>
                              <div className="text-xs text-gray-600">
                                참여:{" "}
                                {run.maxParticipants !== null
                                  ? `${run.currentParticipants || 0}명 / ${
                                      run.maxParticipants
                                    }명`
                                  : `${run.currentParticipants || 0}명`}
                              </div>
                            </div>
                            <span
                              className={`px-2 py-1 rounded text-xs ${
                                status === "진행 중"
                                  ? "bg-green-100 text-green-800"
                                  : status === "종료"
                                  ? "bg-gray-100 text-gray-800"
                                  : status === "모집 마감"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}
                            >
                              {status}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

          {/* 회차 목록 (회차 추가 폼이 닫혔을 때만 표시) */}
          {!isCreatingRun && (
            <div className="space-y-4 mb-6">
              {campaignData.missionAdminCampaign.runs &&
              campaignData.missionAdminCampaign.runs.length > 0 ? (
                [...campaignData.missionAdminCampaign.runs]
                  .sort(
                    (a: any, b: any) =>
                      new Date(a.startAt).getTime() -
                      new Date(b.startAt).getTime()
                  )
                  .map((run: any) => {
                    const isEditing = editingRunId === run.id;
                    // 하루 통일인지 확인 (시작일 00:00, 종료일 다음날 00:00)
                    const startDate = run.startAt
                      ? new Date(run.startAt)
                      : null;
                    const endDate = run.endAt ? new Date(run.endAt) : null;
                    const isFullDay =
                      startDate &&
                      endDate &&
                      startDate.getHours() === 0 &&
                      startDate.getMinutes() === 0 &&
                      endDate.getHours() === 0 &&
                      endDate.getMinutes() === 0 &&
                      endDate.getDate() === startDate.getDate() + 1 &&
                      endDate.getMonth() === startDate.getMonth() &&
                      endDate.getFullYear() === startDate.getFullYear();

                    const startDateLocal = startDate
                      ? startDate.toISOString().slice(0, 10)
                      : "";
                    const endDateLocal = endDate
                      ? isFullDay
                        ? new Date(endDate.getTime() - 24 * 60 * 60 * 1000)
                            .toISOString()
                            .slice(0, 10)
                        : endDate.toISOString().slice(0, 10)
                      : "";
                    const startTimeLocal =
                      startDate && !isFullDay
                        ? `${String(startDate.getHours()).padStart(
                            2,
                            "0"
                          )}:${String(startDate.getMinutes()).padStart(2, "0")}`
                        : "";
                    const endTimeLocal =
                      endDate && !isFullDay
                        ? `${String(endDate.getHours()).padStart(
                            2,
                            "0"
                          )}:${String(endDate.getMinutes()).padStart(2, "0")}`
                        : "";

                    return (
                      <div
                        key={run.id}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        {!isEditing ? (
                          <>
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <div className="font-semibold">
                                  {new Date(run.startAt).toLocaleString(
                                    "ko-KR"
                                  )}{" "}
                                  ~{" "}
                                  {new Date(run.endAt).toLocaleString("ko-KR")}
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  예치금: {run.depositAmount.toLocaleString()}원
                                  | 상금: {run.prizeAmount.toLocaleString()}원
                                  {run.isActive ? (
                                    <span className="ml-2 text-green-600">
                                      활성
                                    </span>
                                  ) : (
                                    <span className="ml-2 text-gray-500">
                                      비활성
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-600 mt-1">
                                  참여 현황:{" "}
                                  {run.maxParticipants !== null
                                    ? `${run.currentParticipants || 0}명 / ${
                                        run.maxParticipants
                                      }명`
                                    : `${run.currentParticipants || 0}명`}
                                  {run.maxParticipants !== null && (
                                    <>
                                      {run.isFull ? (
                                        <span className="ml-2 text-red-600 font-semibold">
                                          (마감)
                                        </span>
                                      ) : run.currentParticipants &&
                                        run.maxParticipants &&
                                        run.currentParticipants >=
                                          run.maxParticipants * 0.8 ? (
                                        <span className="ml-2 text-orange-600 font-semibold">
                                          (마감 임박)
                                        </span>
                                      ) : (
                                        <span className="ml-2 text-green-600">
                                          (참여 가능)
                                        </span>
                                      )}
                                    </>
                                  )}
                                </div>
                                {run.note && (
                                  <div className="text-sm text-gray-500 mt-1">
                                    메모: {run.note}
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setEditingRunId(run.id)}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  편집
                                </button>
                                <button
                                  onClick={() => handleRunDelete(run.id)}
                                  disabled={deletingRun}
                                  className="text-red-600 hover:text-red-800 disabled:opacity-50"
                                >
                                  삭제
                                </button>
                              </div>
                            </div>
                          </>
                        ) : (
                          <form
                            onSubmit={(e) => handleRunUpdate(e, run.id)}
                            className="space-y-4"
                          >
                            <div className="flex items-center mb-4">
                              <input
                                type="checkbox"
                                id={`isFullDay-${run.id}`}
                                name="isFullDay"
                                defaultChecked={isFullDay ?? false}
                                onChange={(e) => {
                                  const timeFields = document.getElementById(
                                    `time-fields-${run.id}`
                                  );
                                  if (timeFields) {
                                    timeFields.style.display = e.target.checked
                                      ? "none"
                                      : "grid";
                                    const startTimeInput =
                                      timeFields.querySelector(
                                        'input[name="startTime"]'
                                      ) as HTMLInputElement;
                                    const endTimeInput =
                                      timeFields.querySelector(
                                        'input[name="endTime"]'
                                      ) as HTMLInputElement;
                                    if (e.target.checked) {
                                      if (startTimeInput)
                                        startTimeInput.removeAttribute(
                                          "required"
                                        );
                                      if (endTimeInput)
                                        endTimeInput.removeAttribute(
                                          "required"
                                        );
                                    } else {
                                      if (startTimeInput)
                                        startTimeInput.setAttribute(
                                          "required",
                                          "required"
                                        );
                                      if (endTimeInput)
                                        endTimeInput.setAttribute(
                                          "required",
                                          "required"
                                        );
                                    }
                                  }
                                }}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <label
                                htmlFor={`isFullDay-${run.id}`}
                                className="ml-2 text-sm font-medium text-gray-700"
                              >
                                하루 통일 (시작일 00:00 ~ 종료일 00:00)
                              </label>
                            </div>

                            <div
                              id={`run-fields-${run.id}`}
                              className="space-y-4"
                            >
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    시작일 *
                                  </label>
                                  <input
                                    type="date"
                                    name="startDate"
                                    required
                                    defaultValue={startDateLocal}
                                    className="w-full px-3 py-3 border border-gray-300 rounded-md"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    종료일 *
                                  </label>
                                  <input
                                    type="date"
                                    name="endDate"
                                    required
                                    defaultValue={endDateLocal}
                                    className="w-full px-3 py-3 border border-gray-300 rounded-md"
                                  />
                                </div>
                              </div>
                              <div
                                id={`time-fields-${run.id}`}
                                className="grid grid-cols-2 gap-4"
                                style={{ display: isFullDay ? "none" : "grid" }}
                              >
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    시작 시간 *
                                  </label>
                                  <input
                                    type="time"
                                    name="startTime"
                                    required={!isFullDay}
                                    step="60"
                                    defaultValue={startTimeLocal}
                                    className="w-full px-3 py-3 border border-gray-300 rounded-md"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    종료 시간 *
                                  </label>
                                  <input
                                    type="time"
                                    name="endTime"
                                    required={!isFullDay}
                                    step="60"
                                    defaultValue={endTimeLocal}
                                    className="w-full px-3 py-3 border border-gray-300 rounded-md"
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  예치금 (원) *
                                </label>
                                <input
                                  type="number"
                                  name="depositAmount"
                                  required
                                  min="0"
                                  defaultValue={run.depositAmount}
                                  className="w-full px-3 py-3 border border-gray-300 rounded-md"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  상금 (원) *
                                </label>
                                <input
                                  type="number"
                                  name="prizeAmount"
                                  required
                                  min="0"
                                  defaultValue={run.prizeAmount}
                                  className="w-full px-3 py-3 border border-gray-300 rounded-md"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                참여 인원수 제한
                              </label>
                              <div className="space-y-3">
                                <div className="flex items-center">
                                  <input
                                    type="radio"
                                    id={`maxParticipants-unlimited-${run.id}`}
                                    name={`maxParticipants-${run.id}`}
                                    value="unlimited"
                                    defaultChecked={
                                      run.maxParticipants === null
                                    }
                                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                  />
                                  <label
                                    htmlFor={`maxParticipants-unlimited-${run.id}`}
                                    className="ml-2 text-sm text-gray-700"
                                  >
                                    무제한
                                  </label>
                                </div>
                                <div className="flex items-center">
                                  <input
                                    type="radio"
                                    id={`maxParticipants-limited-${run.id}`}
                                    name={`maxParticipants-${run.id}`}
                                    value="limited"
                                    defaultChecked={
                                      run.maxParticipants !== null
                                    }
                                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                                    onChange={(e) => {
                                      const valueInput = document.querySelector(
                                        `input[name="maxParticipantsValue"][data-run-id="${run.id}"]`
                                      ) as HTMLInputElement;
                                      if (valueInput) {
                                        valueInput.style.display = e.target
                                          .checked
                                          ? "block"
                                          : "none";
                                        if (e.target.checked) {
                                          valueInput.setAttribute(
                                            "required",
                                            "required"
                                          );
                                        } else {
                                          valueInput.removeAttribute(
                                            "required"
                                          );
                                        }
                                      }
                                    }}
                                  />
                                  <label
                                    htmlFor={`maxParticipants-limited-${run.id}`}
                                    className="ml-2 text-sm text-gray-700"
                                  >
                                    제한 설정
                                  </label>
                                  <input
                                    type="number"
                                    name="maxParticipantsValue"
                                    data-run-id={run.id}
                                    min="1"
                                    defaultValue={run.maxParticipants || ""}
                                    className={`ml-3 px-3 py-2 border border-gray-300 rounded-md w-32 ${
                                      run.maxParticipants === null
                                        ? "hidden"
                                        : ""
                                    }`}
                                    placeholder="최대 인원수"
                                  />
                                </div>
                              </div>
                              {run.maxParticipants !== null && (
                                <div className="mt-2 text-sm text-gray-500">
                                  현재: {run.currentParticipants || 0}명 / 최대:{" "}
                                  {run.maxParticipants}명
                                  {run.isFull && (
                                    <span className="ml-2 text-red-600 font-semibold">
                                      (마감)
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                운영 메모
                              </label>
                              <textarea
                                name="note"
                                defaultValue={run.note || ""}
                                rows={3}
                                className="w-full px-3 py-3 border border-gray-300 rounded-md"
                              />
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="submit"
                                disabled={updatingRun}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                              >
                                {updatingRun ? "저장 중..." : "저장"}
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingRunId(null)}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                              >
                                취소
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                    );
                  })
              ) : (
                <p className="text-gray-500 text-center py-8">
                  등록된 회차가 없습니다.
                </p>
              )}
            </div>
          )}

          {/* 새 회차 추가 */}
          {!isCreatingRun ? (
            <div className="border-t border-gray-200 pt-4">
              <button
                onClick={() => setIsCreatingRun(true)}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                새 회차 추가
              </button>
            </div>
          ) : (
            <div className="border-t border-gray-200 pt-4">
              <h3 className="font-semibold mb-4">새 회차 추가</h3>
              <form
                onSubmit={handleRunCreate}
                className="space-y-4 bg-gray-50 p-4 rounded-lg"
              >
                <div className="space-y-4">
                  {/* 회차 선택 방식 (네모 버튼) */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      회차 선택 방식 *
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setRunInfo({
                            ...runInfo,
                            runType: "separate",
                          });
                        }}
                        className={`px-4 py-3 border-2 rounded-md text-sm font-medium transition-colors ${
                          runInfo.runType === "separate"
                            ? "border-blue-600 bg-blue-50 text-blue-700"
                            : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                        }`}
                      >
                        여러날 따로 생성
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setRunInfo({
                            ...runInfo,
                            runType: "continuous",
                          });
                        }}
                        className={`px-4 py-3 border-2 rounded-md text-sm font-medium transition-colors ${
                          runInfo.runType === "continuous"
                            ? "border-blue-600 bg-blue-50 text-blue-700"
                            : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                        }`}
                      >
                        여러날 이어서 생성
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setRunInfo({
                            ...runInfo,
                            runType: "timeSelect",
                          });
                        }}
                        className={`px-4 py-3 border-2 rounded-md text-sm font-medium transition-colors ${
                          runInfo.runType === "timeSelect"
                            ? "border-blue-600 bg-blue-50 text-blue-700"
                            : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                        }`}
                      >
                        커스터마이징
                      </button>
                    </div>
                  </div>

                  {runInfo.runType === "separate" ? (
                    // 여러날 따로 생성: 여러 날짜 멀티 셀렉
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        날짜 선택 * (여러 날짜 선택 가능)
                      </label>
                      <div className="space-y-2">
                        {runInfo.selectedDates.filter((d) => d).length ===
                          0 && (
                          <button
                            type="button"
                            onClick={() => {
                              const now = new Date();
                              const localNow = new Date(
                                now.getTime() -
                                  now.getTimezoneOffset() * 60 * 1000
                              );
                              const today = localNow
                                .toISOString()
                                .split("T")[0];

                              setRunInfo((prev) => {
                                if (prev.selectedDates.includes(today))
                                  return prev;

                                const firstEmptyIdx =
                                  prev.selectedDates.findIndex((d) => !d);
                                if (firstEmptyIdx >= 0) {
                                  const newDates = [...prev.selectedDates];
                                  newDates[firstEmptyIdx] = today;
                                  return { ...prev, selectedDates: newDates };
                                }

                                return {
                                  ...prev,
                                  selectedDates: [...prev.selectedDates, today],
                                };
                              });
                            }}
                            className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                          >
                            오늘
                          </button>
                        )}
                        {runInfo.selectedDates.map((date, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <input
                              type="date"
                              value={date}
                              onChange={(e) => {
                                const newDates = [...runInfo.selectedDates];
                                newDates[index] = e.target.value;
                                setRunInfo({
                                  ...runInfo,
                                  selectedDates: newDates,
                                });
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            />
                            {date && (
                              <button
                                type="button"
                                onClick={() => {
                                  // 현재 날짜에서 하루를 더한 날짜 계산
                                  const currentDate = new Date(date);
                                  currentDate.setDate(
                                    currentDate.getDate() + 1
                                  );
                                  const nextDate = currentDate
                                    .toISOString()
                                    .split("T")[0];

                                  const newDates = [...runInfo.selectedDates];
                                  newDates.splice(index + 1, 0, nextDate);
                                  setRunInfo({
                                    ...runInfo,
                                    selectedDates: newDates,
                                  });
                                }}
                                className="px-3 py-2 text-green-600 hover:text-green-800 border border-green-300 rounded hover:bg-green-50"
                              >
                                하루 추가
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                const newDates = runInfo.selectedDates.filter(
                                  (_, i) => i !== index
                                );
                                setRunInfo({
                                  ...runInfo,
                                  selectedDates: newDates,
                                });
                              }}
                              className="px-3 py-2 text-red-600 hover:text-red-800 border border-red-300 rounded hover:bg-red-50"
                            >
                              삭제
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            setRunInfo({
                              ...runInfo,
                              selectedDates: [...runInfo.selectedDates, ""],
                            });
                          }}
                          className="w-full px-4 py-2 text-blue-600 border border-blue-300 rounded hover:bg-blue-50"
                        >
                          + 날짜 추가
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        선택한 각 날짜마다 별도의 회차가 생성됩니다 (00:00 ~
                        다음날 00:00)
                      </p>
                    </div>
                  ) : runInfo.runType === "continuous" ? (
                    // 여러날 이어서 생성: 시작일과 종료일만 선택
                    <div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            시작일 *
                          </label>
                          <input
                            type="date"
                            required
                            value={runInfo.startDate}
                            onChange={(e) =>
                              setRunInfo({
                                ...runInfo,
                                startDate: e.target.value,
                              })
                            }
                            className="w-full px-3 py-3 border border-gray-300 rounded-md"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            종료일 (포함) *
                          </label>
                          <input
                            type="date"
                            required
                            value={runInfo.endDate}
                            onChange={(e) =>
                              setRunInfo({
                                ...runInfo,
                                endDate: e.target.value,
                              })
                            }
                            className="w-full px-3 py-3 border border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        시작일부터 종료일까지 하나의 회차가 생성됩니다 (시작일
                        00:00 ~ 종료일 다음날 00:00)
                      </p>
                    </div>
                  ) : (
                    // 커스터마이징: 날짜 + 시간 지정
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            시작일 *
                          </label>
                          <input
                            type="date"
                            required
                            value={runInfo.startDate}
                            onChange={(e) =>
                              setRunInfo({
                                ...runInfo,
                                startDate: e.target.value,
                              })
                            }
                            className="w-full px-3 py-3 border border-gray-300 rounded-md"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            시작 시간 *
                          </label>
                          <input
                            type="time"
                            required
                            step="1800"
                            value={runInfo.startTime}
                            onChange={(e) =>
                              setRunInfo({
                                ...runInfo,
                                startTime: e.target.value,
                              })
                            }
                            className="w-full px-3 py-3 border border-gray-300 rounded-md"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            종료일 *
                          </label>
                          <input
                            type="date"
                            required
                            value={runInfo.endDate}
                            onChange={(e) =>
                              setRunInfo({
                                ...runInfo,
                                endDate: e.target.value,
                              })
                            }
                            className="w-full px-3 py-3 border border-gray-300 rounded-md"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            종료 시간 *
                          </label>
                          <input
                            type="time"
                            required
                            step="1800"
                            value={runInfo.endTime}
                            onChange={(e) =>
                              setRunInfo({
                                ...runInfo,
                                endTime: e.target.value,
                              })
                            }
                            className="w-full px-3 py-3 border border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      예치금 (원) *
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={runInfo.depositAmount}
                      onChange={(e) =>
                        setRunInfo({
                          ...runInfo,
                          depositAmount: e.target.value,
                        })
                      }
                      className="w-full px-3 py-3 border border-gray-300 rounded-md"
                    />
                  </div>

                  {/* 페이백 금액 표시 */}
                  {basicInfo.originalPrice && basicInfo.discountPrice && (
                    <div className="p-4 bg-blue-50 rounded-md">
                      <div className="text-sm text-gray-700">
                        <span className="font-medium">상금(페이백):</span>{" "}
                        <span className="font-bold text-blue-600">
                          {(
                            parseInt(basicInfo.originalPrice) -
                            parseInt(basicInfo.discountPrice)
                          ).toLocaleString()}
                          원
                        </span>
                        <span className="text-xs text-gray-500 ml-2">
                          (캠페인 설정값이 자동으로 적용됩니다)
                        </span>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      참여 인원수 제한
                    </label>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="maxParticipants-unlimited-edit"
                          name="maxParticipants-edit"
                          value="unlimited"
                          checked={runInfo.maxParticipantsType === "unlimited"}
                          onChange={(e) =>
                            setRunInfo({
                              ...runInfo,
                              maxParticipantsType: e.target.value as
                                | "unlimited"
                                | "limited",
                            })
                          }
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <label
                          htmlFor="maxParticipants-unlimited-edit"
                          className="ml-2 text-sm text-gray-700"
                        >
                          무제한
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="maxParticipants-limited-edit"
                          name="maxParticipants-edit"
                          value="limited"
                          checked={runInfo.maxParticipantsType === "limited"}
                          onChange={(e) =>
                            setRunInfo({
                              ...runInfo,
                              maxParticipantsType: e.target.value as
                                | "unlimited"
                                | "limited",
                            })
                          }
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <label
                          htmlFor="maxParticipants-limited-edit"
                          className="ml-2 text-sm text-gray-700"
                        >
                          제한 설정
                        </label>
                        {runInfo.maxParticipantsType === "limited" && (
                          <input
                            type="number"
                            min="1"
                            value={runInfo.maxParticipantsValue}
                            onChange={(e) =>
                              setRunInfo({
                                ...runInfo,
                                maxParticipantsValue: e.target.value,
                              })
                            }
                            className="ml-3 px-3 py-2 border border-gray-300 rounded-md w-32"
                            placeholder="최대 인원수"
                            required={runInfo.maxParticipantsType === "limited"}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      운영 메모
                    </label>
                    <textarea
                      value={runInfo.note}
                      onChange={(e) =>
                        setRunInfo({ ...runInfo, note: e.target.value })
                      }
                      rows={3}
                      className="w-full px-3 py-3 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsCreatingRun(false);
                        setRunInfo({
                          runType: "separate",
                          selectedDates: [],
                          startDate: "",
                          endDate: "",
                          startTime: "",
                          endTime: "",
                          depositAmount: "",
                          note: "",
                          maxParticipantsType: "unlimited",
                          maxParticipantsValue: "",
                        });
                      }}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    >
                      취소
                    </button>
                    <button
                      type="submit"
                      disabled={creatingRun}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      {creatingRun ? "생성 중..." : "생성"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}

          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              이전
            </button>
            <button
              type="button"
              onClick={() => setStep(3)}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              다음 단계
            </button>
          </div>
        </div>
      )}

      {/* Step 3: 미션 단계 */}
      {step === 3 && (
        <form
          onSubmit={handleStep3Submit}
          className="bg-white p-6 rounded-lg shadow w-full max-w-none"
        >
          <h2 className="text-xl font-semibold mb-4">미션 단계 구성</h2>

          {/* 미션 추가 */}
          <div className="mb-6">
            <h3 className="font-semibold mb-4">미션 추가</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {missionTypeOptions.map((option) => {
                const isSelected = selectedMissionTypes.includes(option.value);
                const selectedOrder = isSelected
                  ? selectedMissionTypes.indexOf(option.value) + 1
                  : null;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleMissionTypeToggle(option.value)}
                    className={`relative px-4 py-3 rounded-lg border-2 transition-all ${
                      isSelected
                        ? "bg-green-600 border-green-600 text-white shadow-md"
                        : "bg-green-50 border-green-200 text-green-700 hover:border-green-400 hover:bg-green-100"
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <option.Icon className="text-base" aria-hidden />
                      <div className="font-medium text-sm">{option.label}</div>
                    </div>
                    {selectedOrder !== null && (
                      <>
                        <div className="mt-1 text-xs opacity-90">
                          {selectedOrder}번째
                        </div>
                        <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white text-green-700 flex items-center justify-center text-xs font-bold border border-green-200 shadow">
                          {selectedOrder}
                        </div>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
            {selectedMissionTypes.length > 0 && (
              <p className="mt-3 text-sm text-gray-600">
                {selectedMissionTypes.length}개의 미션이 선택되었습니다.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
            {missionSteps.map((step, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-semibold">
                    {idx + 1}. {step.title || step.type}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const removedStep = missionSteps[idx];
                        const newSteps = [...missionSteps];
                        newSteps.splice(idx, 1);
                        setMissionSteps(newSteps);
                        setSelectedMissionTypes((prev) =>
                          prev.filter((type) => type !== removedStep.type)
                        );
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      삭제
                    </button>
                  </div>
                </div>
                <div className="text-sm text-gray-600 mb-2">
                  타입: {step.type} | 인증: {step.proofType}
                </div>

                {/* 편집 섹션: 항상 펼침 */}
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                  {/* 타이틀 편집 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      타이틀
                    </label>
                    <input
                      type="text"
                      value={step.title || ""}
                      onChange={(e) => {
                        const newSteps = [...missionSteps];
                        newSteps[idx] = {
                          ...newSteps[idx],
                          title: e.target.value,
                        };
                        setMissionSteps(newSteps);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      placeholder="미션 스텝 타이틀을 입력하세요"
                    />
                  </div>

                  {/* Guide 편집 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      가이드 (단계별 안내)
                    </label>
                    <div className="space-y-2">
                      {(step.guide || []).map(
                        (item: string, guideIdx: number) => (
                          <div key={guideIdx} className="flex gap-2">
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => {
                                const newSteps = [...missionSteps];
                                const newGuide = [
                                  ...(newSteps[idx].guide || []),
                                ];
                                newGuide[guideIdx] = e.target.value;
                                newSteps[idx] = {
                                  ...newSteps[idx],
                                  guide: newGuide,
                                };
                                setMissionSteps(newSteps);
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                              placeholder="가이드 항목 입력"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newSteps = [...missionSteps];
                                const newGuide = [
                                  ...(newSteps[idx].guide || []),
                                ];
                                newGuide.splice(guideIdx, 1);
                                newSteps[idx] = {
                                  ...newSteps[idx],
                                  guide: newGuide,
                                };
                                setMissionSteps(newSteps);
                              }}
                              className="px-3 py-2 text-red-600 hover:text-red-800 text-sm"
                            >
                              삭제
                            </button>
                          </div>
                        )
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const newSteps = [...missionSteps];
                          const newGuide = [...(newSteps[idx].guide || []), ""];
                          newSteps[idx] = {
                            ...newSteps[idx],
                            guide: newGuide,
                          };
                          setMissionSteps(newSteps);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + 가이드 항목 추가
                      </button>
                    </div>
                  </div>

                  {/* Requirements 편집 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      필수 요구사항
                    </label>
                    <div className="space-y-2">
                      {(step.requirements || []).map(
                        (item: string, reqIdx: number) => (
                          <div key={reqIdx} className="flex gap-2">
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => {
                                const newSteps = [...missionSteps];
                                const newRequirements = [
                                  ...(newSteps[idx].requirements || []),
                                ];
                                newRequirements[reqIdx] = e.target.value;
                                newSteps[idx] = {
                                  ...newSteps[idx],
                                  requirements: newRequirements,
                                };
                                setMissionSteps(newSteps);
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                              placeholder="필수 요구사항 입력"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newSteps = [...missionSteps];
                                const newRequirements = [
                                  ...(newSteps[idx].requirements || []),
                                ];
                                newRequirements.splice(reqIdx, 1);
                                newSteps[idx] = {
                                  ...newSteps[idx],
                                  requirements: newRequirements,
                                };
                                setMissionSteps(newSteps);
                              }}
                              className="px-3 py-2 text-red-600 hover:text-red-800 text-sm"
                            >
                              삭제
                            </button>
                          </div>
                        )
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const newSteps = [...missionSteps];
                          const newRequirements = [
                            ...(newSteps[idx].requirements || []),
                            "",
                          ];
                          newSteps[idx] = {
                            ...newSteps[idx],
                            requirements: newRequirements,
                          };
                          setMissionSteps(newSteps);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + 요구사항 추가
                      </button>
                    </div>
                  </div>

                  {/* Notices 편집 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      주의사항 (꼭 알아야 할 점)
                    </label>
                    <div className="space-y-2">
                      {(step.notices || []).map(
                        (item: string, noticeIdx: number) => (
                          <div key={noticeIdx} className="flex gap-2">
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => {
                                const newSteps = [...missionSteps];
                                const newNotices = [
                                  ...(newSteps[idx].notices || []),
                                ];
                                newNotices[noticeIdx] = e.target.value;
                                newSteps[idx] = {
                                  ...newSteps[idx],
                                  notices: newNotices,
                                };
                                setMissionSteps(newSteps);
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                              placeholder="주의사항 입력"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newSteps = [...missionSteps];
                                const newNotices = [
                                  ...(newSteps[idx].notices || []),
                                ];
                                newNotices.splice(noticeIdx, 1);
                                newSteps[idx] = {
                                  ...newSteps[idx],
                                  notices: newNotices,
                                };
                                setMissionSteps(newSteps);
                              }}
                              className="px-3 py-2 text-red-600 hover:text-red-800 text-sm"
                            >
                              삭제
                            </button>
                          </div>
                        )
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const newSteps = [...missionSteps];
                          const newNotices = [
                            ...(newSteps[idx].notices || []),
                            "",
                          ];
                          newSteps[idx] = {
                            ...newSteps[idx],
                            notices: newNotices,
                          };
                          setMissionSteps(newSteps);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + 주의사항 추가
                      </button>
                    </div>
                  </div>

                  {/* 안내 이미지 업로드 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      안내 이미지
                    </label>
                    <div className="space-y-3">
                      {/* 기존 이미지 또는 선택한 파일 미리보기 표시 */}
                      {step.imageUrl && (
                        <div className="relative">
                          <img
                            src={
                              typeof step.imageUrl === "string"
                                ? step.imageUrl
                                : URL.createObjectURL(step.imageUrl)
                            }
                            alt="안내 이미지"
                            className="w-full max-w-md rounded-lg object-cover border border-gray-300"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newSteps = [...missionSteps];
                              // File 객체인 경우 URL 해제
                              if (newSteps[idx].imageUrl instanceof File) {
                                URL.revokeObjectURL(
                                  URL.createObjectURL(newSteps[idx].imageUrl)
                                );
                              }
                              newSteps[idx] = {
                                ...newSteps[idx],
                                imageUrl: null,
                              };
                              setMissionSteps(newSteps);
                            }}
                            className="mt-2 px-3 py-1 text-sm text-red-600 hover:text-red-800 border border-red-300 rounded hover:bg-red-50"
                          >
                            이미지 삭제
                          </button>
                        </div>
                      )}
                      {/* 파일 업로드 */}
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const newSteps = [...missionSteps];
                              // 기존 File 객체가 있으면 URL 해제
                              if (newSteps[idx].imageUrl instanceof File) {
                                URL.revokeObjectURL(
                                  URL.createObjectURL(newSteps[idx].imageUrl)
                                );
                              }
                              newSteps[idx] = {
                                ...newSteps[idx],
                                imageUrl: file,
                              };
                              setMissionSteps(newSteps);
                            }
                          }}
                          className="block w-full text-sm text-gray-700 border border-gray-300 rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          이미지 파일을 선택하세요 (JPG, PNG 등)
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Window 설정 안내 */}
                  <div className="p-3 bg-blue-50 rounded-md">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">제출 기간:</span>{" "}
                      회차(Run)의 시작 시간과 종료 시간이 자동으로 적용됩니다.
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {missionSteps.length === 0 && (
              <p className="text-gray-500 text-center py-8 xl:col-span-2">
                미션 단계를 추가해주세요.
              </p>
            )}
          </div>

          {(() => {
            const canEditGuide =
              !campaignData?.missionAdminCampaign?.runs ||
              campaignData.missionAdminCampaign.runs.length === 0 ||
              campaignData.missionAdminCampaign.runs.every(
                (run: any) => run.participationCount === 0
              );

            return (
              <>
                {!canEditGuide && (
                  <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      참여자가 있는 회차가 있어 미션 단계를 수정할 수 없습니다.
                      모든 회차의 참여자가 없을 때만 수정 가능합니다.
                    </p>
                  </div>
                )}
                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="px-6 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                  >
                    이전
                  </button>
                  <button
                    type="submit"
                    disabled={
                      settingSteps || missionSteps.length === 0 || !canEditGuide
                    }
                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {settingSteps ? "저장 중..." : "편집 완료"}
                  </button>
                </div>
              </>
            );
          })()}
        </form>
      )}
    </div>
  );
};

export default MissionAdminCampaignsEdit;
